import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Building, Home } from 'lucide-react';

const CommercialResidentialPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.35 }
  };

  const commercialBenefits = [
    'High traffic durability',
    'Low maintenance costs',
    'Professional appearance',
    'Stain and chemical resistant',
    'Improves lighting efficiency',
    'Long-term cost savings'
  ];

  const residentialBenefits = [
    'Elegant, modern aesthetic',
    'Hypoallergenic surface',
    'Easy to clean',
    'Increases property value',
    'Sustainable and eco-friendly',
    'Customizable finishes'
  ];

  return (
    <div className="min-h-screen pt-16" data-testid="commercial-residential-page">
      {/* Hero */}
      <section className="section-spacing bg-[radial-gradient(1200px_600px_at_50%_50%,#EEEDEC_0%,#F7F7F6_45%,#FAFAF9_100%)]">
        <div className="container-custom text-center">
          <motion.div {...fadeInUp}>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Commercial & Residential Solutions</h1>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              Tailored concrete polishing services for every space type
            </p>
          </motion.div>
        </div>
      </section>

      {/* Commercial Section */}
      <section className="section-spacing bg-background" data-testid="commercial-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <Building className="h-12 w-12 text-primary mb-4" />
              <h2 className="text-3xl font-bold mb-4">Commercial Spaces</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Our commercial polished concrete solutions are designed for high-traffic environments. 
                From retail stores to corporate offices, warehouses to hospitality venues, we deliver 
                durable, professional floors that enhance your business environment while reducing 
                long-term maintenance costs.
              </p>
              <ul className="space-y-3 mb-8">
                {commercialBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button className="btn-primary" data-testid="commercial-cta-btn">
                  Request Commercial Quote
                </Button>
              </Link>
            </motion.div>

            <motion.div {...fadeInUp}>
              <Card className="overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/pk5bpnpo_20250313_160641.jpg"
                  alt="Commercial Space"
                  className="w-full aspect-[4/3] object-cover"
                />
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Residential Section */}
      <section className="section-spacing bg-secondary/20" data-testid="residential-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp} className="order-2 lg:order-1">
              <Card className="overflow-hidden">
                <img
                  src="https://customer-assets.emergentagent.com/job_concrete-vision/artifacts/8tyf58fm_20240213_111427.jpg"
                  alt="Residential Space"
                  className="w-full aspect-[4/3] object-cover"
                />
              </Card>
            </motion.div>

            <motion.div {...fadeInUp} transition={{ delay: 0.1 }} className="order-1 lg:order-2">
              <Home className="h-12 w-12 text-primary mb-4" />
              <h2 className="text-3xl font-bold mb-4">Residential Spaces</h2>
              <p className="text-muted-foreground mb-6 leading-relaxed">
                Transform your home with our premium residential polished concrete services. 
                Perfect for living rooms, kitchens, bedrooms, and outdoor areas, our polished concrete 
                adds a touch of modern elegance while providing practical benefits for Sydney's climate.
              </p>
              <ul className="space-y-3 mb-8">
                {residentialBenefits.map((benefit, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="h-2 w-2 rounded-full bg-primary" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact">
                <Button className="btn-primary" data-testid="residential-cta-btn">
                  Request Residential Quote
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="section-spacing bg-background">
        <div className="container-custom">
          <motion.div className="text-center mb-12" {...fadeInUp}>
            <h2 className="text-3xl font-bold mb-4">Popular Applications</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Polished concrete works beautifully in various settings
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Retail Stores', desc: 'High-traffic, easy-clean surfaces' },
              { title: 'Office Spaces', desc: 'Professional, modern environments' },
              { title: 'Warehouses', desc: 'Durable, industrial-grade floors' },
              { title: 'Restaurants', desc: 'Stain-resistant, hygienic surfaces' },
              { title: 'Luxury Villas', desc: 'Elegant, high-end finishes' },
              { title: 'Showrooms', desc: 'Sleek, reflective aesthetics' }
            ].map((app, index) => (
              <motion.div key={index} {...fadeInUp} transition={{ delay: 0.1 * index }}>
                <Card className="h-full hover:shadow-lg transition-shadow duration-300">
                  <CardContent className="pt-6">
                    <h3 className="font-bold text-lg mb-2">{app.title}</h3>
                    <p className="text-sm text-muted-foreground">{app.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommercialResidentialPage;
